import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchQuestionListDetails,
  fetchQuestionLists,
  fetchQuestionListsForEvaluation,
  fetchUserQuestionLists,
  toggleArchiveStatus,
  setDefaultQuestionList,
  exportQuestionList,
  deleteQuestionList,
  copyQuestionList,
  importQuestionList,
  shareQuestionList,
  createQuestionListFull,
  updateQuestionListFull,
  updateQuestionListPartial,
} from "../Api/questions";
import {
  QuestionListDetailsResponse,
  QuestionListFull,
  QuestionListPartialUpdate,
  QuestionListResponse,
} from "../Models/question";

export const useQuestionListsforUser = (userId: number) => {
  return useQuery<QuestionListResponse, Error>({
    queryKey: ["questionLists", userId],
    queryFn: () => fetchUserQuestionLists(userId),
  });
};

export const useQuestionLists = (cert_category: number | undefined) => {
  const query = useQuery<QuestionListResponse, Error>({
    queryKey: ["questionLists", cert_category],
    queryFn: () => fetchQuestionLists(1, 1000, "", cert_category),
    enabled: !!cert_category,
  });
  return query;
};

export const useQuestionListsForEvaluation = (
  cert_category: number | undefined,
) => {
  return useQuery<QuestionListResponse, Error>({
    queryKey: ["questionListsForEvaluation", cert_category],
    queryFn: () => fetchQuestionListsForEvaluation(1, 1000, "", cert_category),
    enabled: !!cert_category,
  });
};

export const useToggleArchiveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => toggleArchiveStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useSetDefaultQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      questionListId,
      categoryId,
    }: {
      questionListId: number;
      categoryId: number;
    }) => setDefaultQuestionList(questionListId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useExportQuestionList = () => {
  return useMutation({
    mutationFn: ({ id, format }: { id: number; format: "json" | "docx" }) =>
      exportQuestionList(id, format),
  });
};

export const usePaginatedQuestionLists = (
  page: number,
  page_size: number,
  search: string,
  sort_order?: string,
  sort_direction?: "asc" | "desc",
  cert_category?: number | undefined,
) => {
  return useQuery<QuestionListResponse, Error>({
    queryKey: [
      "questionListsPaginated",
      page,
      page_size,
      search,
      sort_order,
      sort_direction,
      cert_category,
    ],
    queryFn: () =>
      fetchQuestionLists(
        page,
        page_size,
        search,
        cert_category,
        false,
        sort_order,
        sort_direction,
      ),
  });
};

export const useDeleteQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteQuestionList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useCopyQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => copyQuestionList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useImportQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => importQuestionList(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useShareQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => shareQuestionList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useCreateQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: QuestionListFull) => createQuestionListFull(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
    },
  });
};

export const useUpdateQuestionList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: QuestionListFull }) =>
      updateQuestionListFull(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
      queryClient.invalidateQueries({ queryKey: ["questionList", id] });
      queryClient.invalidateQueries({ queryKey: ["questionListDetails", id] });
    },
  });
};

export const useUpdateQuestionListPartial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: QuestionListPartialUpdate;
    }) => updateQuestionListPartial(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["questionLists"] });
      queryClient.invalidateQueries({ queryKey: ["questionListsPaginated"] });
      queryClient.invalidateQueries({ queryKey: ["questionList", id] });
      queryClient.invalidateQueries({ queryKey: ["questionListDetails", id] });
    },
  });
};

const useQuestionListDetails = (questionListId: unknown) => {
  return useQuery<QuestionListDetailsResponse, Error>({
    queryKey: ["questionListDetails", Number(questionListId)],
    queryFn: () => fetchQuestionListDetails(Number(questionListId)),
    enabled: !!questionListId,
  });
};

export default useQuestionListDetails;
